import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HatGlasses } from "lucide-react";

import { authClient } from "@/auth/client";
import { useViewerHasPermission } from "@/auth/shared";
import { useTRPC } from "@/integrations/trpc/react";

import { Button, type ButtonProps } from "../base/button";

export function ImpersonateButton({
  userId,
  variant,
  color = "secondary",
  ...props
}: {
  userId?: string | null;
} & Omit<ButtonProps, "children" | "onPress">) {
  const canImpersonate = useViewerHasPermission({
    user: ["impersonate"],
  });

  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutate: impersonate } = useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await authClient.admin.impersonateUser({
        userId: id,
      });

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.viewer.get.queryOptions());
      queryClient.invalidateQueries(trpc.viewer.getImpersonator.queryOptions());
    },
  });

  if (!canImpersonate || !userId) {
    return null;
  }

  return (
    <Button
      {...props}
      color={color}
      variant={variant}
      onPress={() => impersonate(userId)}
      tooltip="Impersonate User's Account"
    >
      <HatGlasses size={14} className="-ml" />
      {variant !== "icon" && "Impersonate"}
    </Button>
  );
}
